/**
 * PR-26 Phase 4 — the web client for the single-collection job callables
 * (mirrors iOS JobsBackend.swift, plan §4).
 *
 * Every job mutation goes through these App Check protected Cloud Functions:
 * direct Firestore writes to companies/{id}/jobs/** are denied by rules.
 * Attachments remain a direct client write while the job is not archived.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { JobState } from '../types/models';

/** Backend contract: 8–128 chars of [A-Za-z0-9_-] (functions/jobsCore.js). */
export function newIdempotencyKey(): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
          .toString(36)
          .slice(2)}`;
  return uuid.replace(/[^A-Za-z0-9_-]/g, '');
}

export interface CreateJobResult {
  success: boolean;
  jobId: string;
  jobNumber: number;
}

/**
 * Creates a job. `jobId` may be pre-generated so evidence can be uploaded
 * against it; the server owns state, jobNumber, registrationKey,
 * schemaVersion, createdBy and the audit entry.
 */
export async function createJob(params: {
  companyId: string;
  jobId?: string;
  fields: Record<string, unknown>;
  idempotencyKey?: string;
}): Promise<CreateJobResult> {
  const call = httpsCallable<Record<string, unknown>, CreateJobResult>(
    functions,
    'createJob'
  );
  const res = await call({
    ...params.fields,
    companyId: params.companyId,
    ...(params.jobId ? { jobId: params.jobId } : {}),
    idempotencyKey:
      params.idempotencyKey ??
      (params.jobId ? `create-${params.jobId}` : newIdempotencyKey()),
  });
  return res.data;
}

/** Updates editable fields; role allowlists are enforced server-side. */
export async function updateJob(params: {
  companyId: string;
  jobId: string;
  updates: Record<string, unknown>;
}): Promise<void> {
  const call = httpsCallable<
    {
      companyId: string;
      jobId: string;
      updates: Record<string, unknown>;
      idempotencyKey: string;
    },
    { success: boolean }
  >(functions, 'updateJob');
  await call({
    companyId: params.companyId,
    jobId: params.jobId,
    updates: params.updates,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Requests a state transition; the server's state machine decides. */
export async function changeJobState(params: {
  companyId: string;
  jobId: string;
  state: JobState;
}): Promise<{ success: boolean; fromState: JobState; toState: JobState }> {
  const call = httpsCallable<
    { companyId: string; jobId: string; state: JobState; idempotencyKey: string },
    { success: boolean; fromState: JobState; toState: JobState }
  >(functions, 'changeJobState');
  const response = await call({
    companyId: params.companyId,
    jobId: params.jobId,
    state: params.state,
    idempotencyKey: newIdempotencyKey(),
  });
  return response.data;
}

/** Office-role correction: hide evidence while retaining bytes, record and audit. */
export async function excludeJobImage(params: {
  companyId: string;
  jobId: string;
  imageId: string;
  reason: string;
}): Promise<void> {
  const call = httpsCallable<typeof params, { success: boolean; imageId: string }>(
    functions,
    'excludeJobImage'
  );
  await call(params);
}

/** Office-role correction: relabel evidence without changing its bytes or checksum. */
export async function reclassifyJobImage(params: {
  companyId: string;
  jobId: string;
  imageId: string;
  newType: string;
  reason: string;
}): Promise<void> {
  const call = httpsCallable<
    typeof params,
    { success: boolean; imageId: string; fromType: string; toType: string }
  >(functions, 'reclassifyJobImage');
  await call(params);
}

/** Superadmin-only, audited, irreversible. */
export async function deleteJobPermanently(params: {
  companyId: string;
  jobId: string;
}): Promise<{ success: boolean; storageCleanupPending?: boolean }> {
  const call = httpsCallable<
    { companyId: string; jobId: string },
    { success: boolean; storageCleanupPending?: boolean }
  >(functions, 'deleteJobPermanently');
  const res = await call(params);
  return res.data;
}
